// AreaOpsModal — the "Alan İşlemleri" full-screen modal.
//
// Layout (per design): big image CENTER, NO left version/history rail, RIGHT
// sidebar = the 4 selectable area features + the pin queue. Owns the queue hook
// so the center image reflects the chain (currentUrl) live, and mounts the pin
// overlay glued to the center <img>. Opened straight from the right-rail AI
// Wizard tab on an image; the wizard-only operations are cards here too and
// hand off to ImageAnimatorModal (this modal closes first — no popup-over-popup).

import { Fragment, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import {
  LuX,
  LuLoaderCircle,
  LuUpload,
  LuCheck,
  LuTriangleAlert,
  LuArrowRight,
  LuWandSparkles,
  LuShapes,
  LuImagePlus,
} from "react-icons/lu";
import useAreaOpsQueue from "./useAreaOpsQueue";
import {
  AREA_OP_FEATURES,
  AREA_OPS_COVERED_WIZARD_IDS,
  getAreaOpFeature,
  isAreaOpReady,
  uploadReferenceImage,
} from "./areaOpsSubmit";
import {
  AREA_OPS_BATCH_OPERATION,
  MAX_BATCH_OPS,
  estimateAreaOpsCredit,
  formatAreaOpsBatchLabel,
} from "./areaOpsBatch";
import AreaOpsPinOverlay from "./AreaOpsPinOverlay";
import AreaOpsApplyAllButton from "./AreaOpsApplyAllButton";
import BrandLogoPicker from "./BrandLogoPicker";
import { IMAGE_EDIT_FEATURES } from "../ImageAnimatorModal";
import { notifyInsufficientBalance } from "../../../utils/insufficientBalance";
import SceneAudioService from "../../../services/sceneAudioService";
import "../../timeline/IdeaTimeline/components/AnimationPanel/index.scss";
import "./_areaOps.scss";

// Icons for area ops that are NOT in the Wizard-AI grid (IMAGE_EDIT_FEATURES),
// so they have no shared entry to borrow an icon from. "edit-region" (Prompt ile
// Düzelt) is area-ops-only on purpose — whole-image prompt edit already exists as
// "Edit Image" in the wizard.
const AREA_OP_LOCAL_ICONS = {
  "edit-region": LuWandSparkles,
  "add-object": LuShapes,
  "add-logo": LuImagePlus,
};

// Reuse each feature's Wizard-AI icon (single source of truth) so the sidebar
// cards match the Aidea Wizard AI grid. Features absent from the grid fall back
// to a local icon. The name is the area op's own — the same i18n key as the
// wizard's for shared ops; Yeniden Boyutlandır / Benzer İnfografik are shorter,
// and `cardName` shortens a label on the card alone (popup + history keep name).
// Hidden from the card grid (the feature itself stays in AREA_OP_FEATURES):
// without Benzer İnfografik the grid fits the column, so the draw hint no
// longer pushes a scrollbar in and shifts the cards.
const HIDDEN_AREA_CARD_IDS = new Set(["generate-from-reference"]);

const AREA_CARDS = AREA_OP_FEATURES.filter((f) => !HIDDEN_AREA_CARD_IDS.has(f.id)).map((f) => {
  const full = IMAGE_EDIT_FEATURES.find((x) => x.id === f.id);
  return {
    id: f.id,
    Icon: full?.icon || AREA_OP_LOCAL_ICONS[f.id],
    name: f.cardName || f.name,
  };
});

// The Wizard-AI operations this modal cannot host (Metni Ayır, Aidea Akıllı
// Öğeler, Görseli Canlandır, Benzer Oluştur): their result is not one image that
// becomes the next version. Listed after the area cards; a click closes this
// modal and opens the wizard on that operation (no popup over a popup).
const WIZARD_CARDS = IMAGE_EDIT_FEATURES.filter(
  (f) => !AREA_OPS_COVERED_WIZARD_IDS.has(f.id) && !f.comingSoon
).map((f) => ({ id: f.id, Icon: f.icon, name: f.name }));

// Whole-image pickers — the AI Wizard's options (ImageAnimatorModal resize /
// upscale forms); the outpaint prompt per ratio is built server-side.
const ASPECT_RATIOS = ["1:1", "4:3", "4:5", "16:9", "9:16"];
const SCALE_FACTORS = [
  { value: 2, hint: "HD" },
  { value: 3, hint: "Full HD" },
  { value: 4, hint: "4K" },
];

const LANGUAGES = [
  { value: "Turkish", label: "Türkçe" },
  { value: "English", label: "İngilizce" },
  { value: "Spanish", label: "İspanyolca" },
  { value: "German", label: "Almanca" },
  { value: "French", label: "Fransızca" },
  { value: "Italian", label: "İtalyanca" },
  { value: "Portuguese", label: "Portekizce" },
  { value: "Arabic", label: "Arapça" },
  { value: "Russian", label: "Rusça" },
  { value: "Azerbaijani", label: "Azerice" },
];

const STATUS_LABEL = {
  draft: "AreaOps.StatusDraft",
  staged: "AreaOps.StatusStaged",
  queued: "AreaOps.StatusQueued",
  running: "AreaOps.StatusRunning",
  done: "AreaOps.StatusDone",
  failed: "AreaOps.StatusFailed",
};

// "Tümünü Uygula" floats just under the photo — it never takes layout room, so
// the photo keeps its size. Gap below the photo and the button's height (the
// app-modal primary button, same as ImageAnimatorModal's "Başlat").
const APPLY_GAP_PX = 12;
const APPLY_BTN_PX = 40;

// Fade-out on a hand-off to the wizard — the .is-leaving keyframes in _areaOps.scss.
const HANDOFF_EXIT_MS = 240;

// i18n.t is not bound to its instance; the batch label helper calls t bare.
const t = (key, opts) => i18n.t(key, opts);

// A batch op's failure code (details.ops[].error) as user text; any other
// message (network, server) is shown as is.
const OP_ERROR_TEXT = {
  unchanged: "AreaOps.OpUnchanged",
  "marker-ink": "AreaOps.OpMarkerInk",
};
const opErrorText = (error) => {
  if (OP_ERROR_TEXT[error]) return i18n.t(OP_ERROR_TEXT[error]);
  return /^logo: /.test(error) ? i18n.t("AreaOps.OpLogoFailed") : error;
};

export default function AreaOpsModal({
  visible,
  boxId,
  imageUrl,
  trainingId,
  navId,
  sceneIndex,
  initialFeatureId,
  selectionToken,
  onApply,
  onClose,
  onOpenWizardFeature,
}) {
  const imgRef = useRef(null);
  const stageRef = useRef(null);

  const ctx = useMemo(
    () => ({ trainingId, navId, sceneIndex, boxId }),
    [trainingId, navId, sceneIndex, boxId]
  );
  // The global balance popup (react-bootstrap .modal, z 3000000) opens BELOW this
  // full-screen modal (3000050) and would still take the keyboard focus. Step
  // aside first, as ImageAnimatorModal does: the staged ops are saved and come
  // back when the modal is reopened.
  const showBalancePopup = useCallback(
    (errData) => {
      onClose?.();
      notifyInsufficientBalance(errData);
    },
    [onClose]
  );
  const {
    pins,
    currentUrl,
    originalUrl: trueOriginal,
    busy,
    addDraft,
    updateDraftConfig,
    updateDraftRegion,
    removePin,
    revertToPin,
    revertToVersion,
    revertToOriginal,
    stagedPins,
    batchAvailable,
    stagePin,
    unstagePin,
    applyStaged,
  } = useAreaOpsQueue({
    boxId,
    originalUrl: imageUrl,
    ctx,
    onApply,
    onInsufficientBalance: showBalancePopup,
  });

  const [drawFeature, setDrawFeature] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  // A wizard card was clicked: fading out over the wizard (see HANDOFF_EXIT_MS).
  const [leaving, setLeaving] = useState(false);
  // Rendered image height → the side columns are sized to match it (request:
  // history + features boxes as tall as the photo, not the full viewport).
  const [imgH, setImgH] = useState(0);
  // Where "Tümünü Uygula" sits in the stage ({ top, left } px), from the photo box.
  const [applyPos, setApplyPos] = useState(null);
  // Credit estimate for the staged ops ({ requiredCredits, availableCredits,
  // hasSufficientBalance } or null when unavailable).
  const [credit, setCredit] = useState(null);
  // The batch route answered 404 during this session → ops run one by one.
  const batchLost = useRef(batchAvailable).current && !batchAvailable;

  // Enter draw mode for the feature the wizard opened us with; re-fires on a new
  // selection (selectionToken changes) even if already mounted.
  useEffect(() => {
    if (initialFeatureId) {
      const f = getAreaOpFeature(initialFeatureId);
      if (f) setDrawFeature(f);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionToken]);

  // Esc closes the modal (only when not mid-draw — the overlay owns Esc then —
  // and not while handing off: the wizard underneath owns it by then).
  useEffect(() => {
    if (!visible) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !drawFeature && !leaving) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [visible, drawFeature, leaving, onClose]);

  // Hand-off to the wizard: the host opens it right away, under this modal, and
  // this modal fades out over it (.is-leaving) before closing itself — a
  // crossfade, never two popups at rest.
  useEffect(() => {
    if (!leaving) return undefined;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const timer = setTimeout(() => onClose?.(), reduce ? 0 : HANDOFF_EXIT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaving]);

  // Track the rendered image height so the side columns can match it.
  useEffect(() => {
    if (!visible) return undefined;
    const el = imgRef.current;
    if (!el) return undefined;
    const update = () => {
      const r = el.getBoundingClientRect();
      setImgH(Math.round(r.height));
      // Just under the photo; when the photo already fills the stage height,
      // over its bottom edge instead (still never shrinking it).
      const stage = stageRef.current;
      if (!stage) return;
      const s = stage.getBoundingClientRect();
      const below = r.bottom - s.top + APPLY_GAP_PX;
      const top =
        below + APPLY_BTN_PX <= s.height ? below : r.bottom - s.top - APPLY_BTN_PX - APPLY_GAP_PX;
      setApplyPos({ top: Math.round(top), left: Math.round(r.left - s.left + r.width / 2) });
    };
    update();
    const onLoad = () => update();
    el.addEventListener("load", onLoad);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("load", onLoad);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [visible, currentUrl]);

  // "Uygula" under the photo — the only way to run an op (the draft popup only
  // lists it), so it shows as soon as one op is staged. It sends the first 8
  // (batchPins): ops a batch handed back can push the list past 8, and the rest
  // go next time.
  const showApplyAll = stagedPins.length > 0;
  const batchPins = stagedPins.slice(0, MAX_BATCH_OPS);

  // Re-estimate 400ms after the staged set changes (which ops, not their text:
  // the price depends only on the op types) — only while the button shows.
  const creditKey = showApplyAll ? batchPins.map((p) => p.featureId).join(",") : "";
  useEffect(() => {
    setCredit(null);
    if (!visible || !batchAvailable || !creditKey) return undefined;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      estimateAreaOpsCredit(batchPins, { signal: controller.signal })
        .catch(() => null)
        .then((c) => {
          if (controller.signal.aborted) return;
          setCredit(c);
        });
    }, 400);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, batchAvailable, creditKey]);

  // "Tümünü Uygula" on a "not enough credit" estimate: that figure may be stale
  // (topped up since), so ask again. Only a fresh "no" opens the balance popup
  // (it reads the figures from .details); no answer lets the server decide.
  const recheckRef = useRef(false);
  const onInsufficientCredit = async () => {
    if (recheckRef.current) return;
    recheckRef.current = true;
    const fresh = await estimateAreaOpsCredit(batchPins).catch(() => null);
    recheckRef.current = false;
    if (fresh?.hasSufficientBalance === false) showBalancePopup({ details: fresh });
    else applyStaged();
  };

  // The scene's narration for "Benzer İnfografik Oluştur" (the wizard sends it
  // along with the prompt). Best effort — the job runs without it too.
  const fillNarration = useCallback(
    (pinId) => {
      if (!trainingId || !navId) return;
      SceneAudioService.getSceneAudios({ trainingId })
        .then((data) => {
          const scenes = Array.isArray(data?.scenes) ? data.scenes : [];
          const scene = scenes.find((s) => s.navId === navId);
          const audio = scene?.audios?.find((a) => a.narrationText) || scene?.audios?.[0];
          if (audio?.narrationText) updateDraftConfig(pinId, { narration: audio.narrationText });
        })
        .catch(() => {});
    },
    [trainingId, navId, updateDraftConfig]
  );

  // An open whole-image draft's popup sits in one fixed spot on the photo —
  // picking another card drops it (it was never added to the list).
  const dropWholeDrafts = useCallback(() => {
    pins.filter((p) => p.status === "draft" && p.whole).forEach((p) => removePin(p.id));
  }, [pins, removePin]);

  // A whole-image draft (no region): its popup opens on the photo at once.
  const openWholeDraft = useCallback(
    (featureId) => {
      dropWholeDrafts();
      setDrawFeature(null);
      const id = addDraft(featureId, { whole: true });
      if (featureId === "generate-from-reference") fillNarration(id);
    },
    [dropWholeDrafts, addDraft, fillNarration]
  );

  // A whole-only card opens its popup; any other enters draw mode (toggle).
  const startDraw = useCallback(
    (featureId) => {
      const f = getAreaOpFeature(featureId);
      if (f?.kind === "whole") {
        openWholeDraft(featureId);
        return;
      }
      dropWholeDrafts();
      setDrawFeature((cur) => (cur?.id === featureId ? null : f));
    },
    [openWholeDraft, dropWholeDrafts]
  );

  const onArea = useCallback(
    (area) => {
      if (!drawFeature) return;
      addDraft(drawFeature.id, area);
      setDrawFeature(null);
    },
    [drawFeature, addDraft]
  );

  const onReferenceUpload = useCallback(
    async (pinId, file) => {
      if (!file) return;
      setUploadError(null);
      setUploadingId(pinId);
      try {
        const url = await uploadReferenceImage(file);
        updateDraftConfig(pinId, { referenceImageUrl: url });
      } catch (err) {
        setUploadError(err?.message || i18n.t("Failed to upload image"));
      } finally {
        setUploadingId(null);
      }
    },
    [updateDraftConfig]
  );

  // Drag & drop onto an upload zone — accept the first dropped image file and
  // route it through the same upload path as the file picker.
  const onReferenceDragOver = useCallback((pinId, e) => {
    e.preventDefault();
    setDraggingId(pinId);
  }, []);

  const onReferenceDragLeave = useCallback((pinId, e) => {
    e.preventDefault();
    setDraggingId((cur) => (cur === pinId ? null : cur));
  }, []);

  const onReferenceDrop = useCallback(
    (pinId, e) => {
      e.preventDefault();
      setDraggingId(null);
      const file = e.dataTransfer?.files?.[0];
      if (file && file.type.startsWith("image/")) onReferenceUpload(pinId, file);
    },
    [onReferenceUpload]
  );

  const renderConfig = (pin) => {
    const feature = getAreaOpFeature(pin.featureId);
    if (!feature) return null;
    const cfg = pin.config || {};

    if (feature.id === "replace-product") {
      return (
        <div className="aops-cfg">
          <label className="aops-lbl">{i18n.t("AreaOps.ReferenceImage")}</label>
          {cfg.referenceImageUrl ? (
            <div className="aops-ref">
              <img src={cfg.referenceImageUrl} alt="reference" />
              <button
                type="button"
                className="aops-link"
                onClick={() => updateDraftConfig(pin.id, { referenceImageUrl: "" })}
              >
                {i18n.t("AreaOps.RemoveReference")}
              </button>
            </div>
          ) : (
            <label
              className={`aops-upload ${draggingId === pin.id ? "is-dragging" : ""}`}
              onDragOver={(e) => onReferenceDragOver(pin.id, e)}
              onDragLeave={(e) => onReferenceDragLeave(pin.id, e)}
              onDrop={(e) => onReferenceDrop(pin.id, e)}
            >
              {uploadingId === pin.id ? (
                <LuLoaderCircle size={14} className="aops-spin" />
              ) : (
                <LuUpload size={14} />
              )}
              <span>{i18n.t("AreaOps.UploadReference")}</span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                hidden
                onChange={(e) => onReferenceUpload(pin.id, e.target.files?.[0])}
              />
            </label>
          )}
          <input
            className="aops-input"
            type="text"
            placeholder={i18n.t("AreaOps.TargetObjectPlaceholder")}
            value={cfg.targetObject || ""}
            onChange={(e) => updateDraftConfig(pin.id, { targetObject: e.target.value })}
          />
          <input
            className="aops-input"
            type="text"
            placeholder={i18n.t("AreaOps.PromptOptional")}
            value={cfg.prompt || ""}
            onChange={(e) => updateDraftConfig(pin.id, { prompt: e.target.value })}
          />
        </div>
      );
    }

    if (feature.id === "remove-object") {
      return (
        <div className="aops-cfg">
          <input
            className="aops-input"
            type="text"
            placeholder={i18n.t(
              pin.whole
                ? "AreaOps.RemoveObjectWholePromptPlaceholder"
                : "AreaOps.RemoveObjectPromptPlaceholder"
            )}
            value={cfg.prompt || ""}
            onChange={(e) => updateDraftConfig(pin.id, { prompt: e.target.value })}
          />
        </div>
      );
    }

    if (feature.id === "translate") {
      return (
        <div className="aops-cfg">
          <label className="aops-lbl">{i18n.t("AreaOps.TargetLanguage")}</label>
          <select
            className="aops-input"
            value={cfg.targetLanguage || ""}
            onChange={(e) => updateDraftConfig(pin.id, { targetLanguage: e.target.value })}
          >
            <option value="">{i18n.t("AreaOps.SelectLanguage")}</option>
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (feature.id === "change-image-text") {
      return (
        <div className="aops-cfg">
          <input
            className="aops-input"
            type="text"
            placeholder={i18n.t("AreaOps.OriginalTextPlaceholder")}
            value={cfg.originalText || ""}
            onChange={(e) => updateDraftConfig(pin.id, { originalText: e.target.value })}
          />
          <input
            className="aops-input"
            type="text"
            placeholder={i18n.t("AreaOps.NewTextPlaceholder")}
            value={cfg.newText || ""}
            onChange={(e) => updateDraftConfig(pin.id, { newText: e.target.value })}
          />
        </div>
      );
    }

    if (feature.id === "edit-region") {
      return (
        <div className="aops-cfg">
          <textarea
            className="aops-input"
            rows={3}
            placeholder={i18n.t("AreaOps.EditRegionPromptPlaceholder")}
            value={cfg.prompt || ""}
            onChange={(e) => updateDraftConfig(pin.id, { prompt: e.target.value })}
          />
        </div>
      );
    }

    if (feature.id === "add-object") {
      return (
        <div className="aops-cfg">
          <textarea
            className="aops-input"
            rows={3}
            placeholder={i18n.t("AreaOps.AddObjectPromptPlaceholder")}
            value={cfg.prompt || ""}
            onChange={(e) => updateDraftConfig(pin.id, { prompt: e.target.value })}
          />
          <label className="aops-check">
            <input
              type="checkbox"
              checked={!!cfg.useFullImageReference}
              onChange={(e) =>
                updateDraftConfig(pin.id, {
                  useFullImageReference: e.target.checked,
                })
              }
            />
            <span>{i18n.t("AreaOps.UseFullImageReference")}</span>
          </label>
        </div>
      );
    }

    if (feature.id === "add-logo") {
      return (
        <div className="aops-cfg">
          <label className="aops-lbl">{i18n.t("AreaOps.LogoImage")}</label>
          {cfg.referenceImageUrl ? (
            <div className="aops-ref">
              <img src={cfg.referenceImageUrl} alt="logo" />
              <button
                type="button"
                className="aops-link"
                onClick={() => updateDraftConfig(pin.id, { referenceImageUrl: "" })}
              >
                {i18n.t("AreaOps.RemoveReference")}
              </button>
            </div>
          ) : (
            <>
              <label
                className={`aops-upload ${draggingId === pin.id ? "is-dragging" : ""}`}
                onDragOver={(e) => onReferenceDragOver(pin.id, e)}
                onDragLeave={(e) => onReferenceDragLeave(pin.id, e)}
                onDrop={(e) => onReferenceDrop(pin.id, e)}
              >
                {uploadingId === pin.id ? (
                  <LuLoaderCircle size={14} className="aops-spin" />
                ) : (
                  <LuUpload size={14} />
                )}
                <span>{i18n.t("AreaOps.UploadLogo")}</span>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={(e) => onReferenceUpload(pin.id, e.target.files?.[0])}
                />
              </label>
              {/* Or one of the organization's brand-library logos. */}
              <BrandLogoPicker
                onPick={(url) => updateDraftConfig(pin.id, { referenceImageUrl: url })}
              />
            </>
          )}
          {/* "Tüm görsellere uygula": stamp this logo at the SAME relative
              position on every static image in the training (scene images +
              backgrounds). Routes to the training-wide bulk endpoint. */}
          <label className="aops-check">
            <input
              type="checkbox"
              checked={!!cfg.applyToAllImages}
              onChange={(e) =>
                updateDraftConfig(pin.id, {
                  applyToAllImages: e.target.checked,
                })
              }
            />
            <span>{i18n.t("AreaOps.ApplyLogoToAllImages")}</span>
          </label>
        </div>
      );
    }

    if (feature.id === "upscale") {
      return (
        <div className="aops-cfg">
          <label className="aops-lbl">{i18n.t("Scale Factor")}</label>
          <div className="aops-choices">
            {SCALE_FACTORS.map(({ value, hint }) => (
              <button
                key={value}
                type="button"
                className={`aops-choice ${cfg.scaleFactor === value ? "is-active" : ""}`}
                onClick={() => updateDraftConfig(pin.id, { scaleFactor: value })}
              >
                <span className="aops-choice-main">{`${value}×`}</span>
                <span className="aops-choice-sub">{hint}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (feature.id === "resize") {
      return (
        <div className="aops-cfg">
          <label className="aops-lbl">{i18n.t("Aspect Ratio")}</label>
          <div className="aops-choices">
            {ASPECT_RATIOS.map((ratio) => (
              <button
                key={ratio}
                type="button"
                className={`aops-choice ${cfg.aspectRatio === ratio ? "is-active" : ""}`}
                onClick={() => updateDraftConfig(pin.id, { aspectRatio: ratio })}
              >
                <span className="aops-choice-main">{ratio}</span>
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (feature.id === "generate-from-reference") {
      return (
        <div className="aops-cfg">
          <textarea
            className="aops-input"
            rows={3}
            placeholder={i18n.t("GenerateFromReferencePromptPlaceholder")}
            value={cfg.prompt || ""}
            onChange={(e) => updateDraftConfig(pin.id, { prompt: e.target.value })}
          />
        </div>
      );
    }

    return null; // remove-text / remove-background: no extra input
  };

  // A pin's feature name; a two-mode op run on the whole image says so.
  const opName = (pin) => {
    const feature = getAreaOpFeature(pin.featureId);
    if (!feature) return pin.featureId;
    const name = i18n.t(feature.name);
    return pin.whole && feature.wholeImage ? `${name} · ${i18n.t("AreaOps.WholeImage")}` : name;
  };

  // "Listeye Ekle" tooltip: why the list is closed to more ops (if it is).
  const stageBlockedReason = () =>
    stagedPins.length >= MAX_BATCH_OPS
      ? i18n.t("AreaOps.MaxOpsReached", { max: MAX_BATCH_OPS })
      : undefined;

  // Draft popup content — rendered ON the image (by the overlay) near the drawn
  // region: feature title + its config inputs + Vazgeç / Listeye Ekle. No
  // "Uygula" here: every op goes through the list and runs from the "Uygula"
  // under the photo — the batch takes what it can, the rest run on their own.
  const renderDraftPopup = (pin) => {
    const feature = getAreaOpFeature(pin.featureId);
    const canStage =
      isAreaOpReady(feature, pin.config, { whole: !!pin.whole }) &&
      stagedPins.length < MAX_BATCH_OPS;
    return (
      <>
        <div className="aops-popup-title">{opName(pin)}</div>
        {renderConfig(pin)}
        {/* Why this op's last batch run failed (it came back as a staged box). */}
        {pin.error && <div className="aops-error aops-error--row">{opErrorText(pin.error)}</div>}
        <div className="aops-popup-actions">
          <button type="button" className="aops-link" onClick={() => removePin(pin.id)}>
            {i18n.t("Cancel")}
          </button>
          <button
            type="button"
            className="aops-apply"
            disabled={!canStage}
            title={stageBlockedReason()}
            onClick={() => stagePin(pin.id)}
          >
            {i18n.t("AreaOps.AddToList")}
          </button>
        </div>
      </>
    );
  };

  if (!visible) return null;

  // One sidebar card — same look as the Aidea Wizard AI grid.
  const featureCard = ({ card, active, blocked, blockedHint, testId, onClick }) => {
    const Icon = card.Icon;
    return (
      <div
        key={card.id}
        data-testid={testId}
        className={`ap-card ${active ? "aops-card-active" : ""}`}
        aria-disabled={blocked}
        title={blocked ? blockedHint : undefined}
        onClick={blocked ? undefined : onClick}
        style={{
          width: "100%",
          minWidth: 0,
          cursor: blocked ? "default" : "pointer",
          opacity: blocked ? 0.5 : 1,
          position: "relative",
        }}
      >
        <div className="ap-card-thumb">
          <span className="ap-card-demo">{Icon ? <Icon size={20} /> : null}</span>
        </div>
        <span
          className="ap-card-name"
          style={{
            whiteSpace: "normal",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            width: "100%",
            minHeight: "2.6em",
          }}
        >
          {i18n.t(card.name)}
        </span>
      </div>
    );
  };

  // Side columns match the photo.
  const colStyle = imgH ? { height: imgH, maxHeight: imgH } : undefined;

  // History rows: a draft is configured in its popup on the image, and a staged
  // op is a box there too — listed only once the batch path is gone, so it can
  // still be removed.
  // A staged whole-image step has no box, so it is listed here (with its remove X).
  const inHistory = (pin) =>
    pin.status !== "draft" && !(pin.status === "staged" && batchAvailable && !pin.whole);
  // Version badges follow the list order (Original = v1), not the stored
  // pin.num, which keeps growing with every draft — cancelled ones too.
  // A sequential batch's intermediate steps are versions too, numbered before it.
  const versionNo = new Map();
  let nextVersion = 2;
  pins.filter(inHistory).forEach((pin) => {
    if (pin.status === "done") {
      (pin.steps || []).forEach((_, k) => versionNo.set(`${pin.id}:${k}`, nextVersion++));
    }
    versionNo.set(pin.id, nextVersion++);
  });

  // One version row; the revert ARROW (not the row) jumps to it.
  const versionRow = ({ key, url, name, version, onRevert, failed }) => {
    const isCur = currentUrl === url;
    return (
      <div key={key} className={`aops-ver ${isCur ? "is-current" : ""}`} title={name}>
        <div className="aops-ver-thumbwrap">
          <img className="aops-ver-thumb" src={url} alt={name} draggable={false} />
          <span className="aops-ver-vbadge">{version}</span>
        </div>
        <span className="aops-ver-label">
          {name}
          {failed && <LuTriangleAlert size={12} className="aops-bad" />}
        </span>
        {isCur ? (
          <LuCheck size={14} className="aops-ok" />
        ) : (
          <button
            type="button"
            className="aops-ver-btn"
            title={i18n.t("AreaOps.RevertToVersion")}
            onClick={onRevert}
          >
            <LuArrowRight size={16} />
          </button>
        )}
      </div>
    );
  };

  // A wizard card: open the wizard now (under this modal) and fade out over it.
  const handOffToWizard = (featureId) => {
    if (leaving) return;
    setLeaving(true);
    onOpenWizardFeature(featureId);
  };

  return createPortal(
    <div
      className={`area-ops-modal ${leaving ? "is-leaving" : ""}`}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="aops-modal-backdrop"
        onMouseDown={() => !drawFeature && !leaving && onClose?.()}
      />

      <div className="aops-modal-shell">
        {/* Header — başlık yok (istek); sadece kapat düğmesi sağ üstte. */}
        <div className="aops-modal-header">
          <button
            type="button"
            className="aops-modal-close"
            title={i18n.t("Cancel")}
            onClick={onClose}
          >
            <LuX size={18} />
          </button>
        </div>

        <div className="aops-modal-body">
          {/* CENTER: image, and "Tümünü Uygula" under it once 2+ ops are staged. */}
          <div className="aops-modal-stage" ref={stageRef}>
            <img
              ref={imgRef}
              className="aops-modal-image"
              src={currentUrl}
              alt="area ops"
              draggable={false}
            />
            {/* Portaled to <body>, so it can't fade with the shell: gone as
                soon as a hand-off starts. */}
            {!leaving && (
              <AreaOpsPinOverlay
                anchorRef={imgRef}
                pins={pins}
                drawFeature={drawFeature}
                onArea={onArea}
                onCancelDraw={() => setDrawFeature(null)}
                renderDraftPopup={renderDraftPopup}
                onPinClick={unstagePin}
                onResizeDraft={updateDraftRegion}
              />
            )}
            {showApplyAll && applyPos && (
              <div className="aops-stage-apply" style={applyPos}>
                <AreaOpsApplyAllButton
                  credit={credit}
                  busy={busy}
                  onApplyAll={applyStaged}
                  onInsufficient={onInsufficientCredit}
                />
              </div>
            )}
          </div>

          {/* RIGHT: selectable features + queue. */}
          <div className="aops-modal-side" style={colStyle}>
            {/* Feature cards — same look as the Aidea Wizard AI grid. */}
            <div
              className="ap-panel aops-feature-panel"
              style={{ height: "auto", overflow: "visible" }}
            >
              <div
                className="ap-grid"
                style={{
                  padding: "4px 0",
                  overflowY: "visible",
                  flex: "none",
                  alignItems: "start",
                }}
              >
                {AREA_CARDS.map((f) =>
                  featureCard({
                    card: f,
                    active: drawFeature?.id === f.id,
                    // A new ratio would shift the boxes waiting in the list.
                    blocked: f.id === "resize" && stagedPins.length > 0,
                    blockedHint: i18n.t("AreaOps.ResizeBlockedByStaged"),
                    onClick: () => startDraw(f.id),
                  })
                )}
                {typeof onOpenWizardFeature === "function" &&
                  WIZARD_CARDS.map((f) =>
                    featureCard({
                      card: f,
                      testId: `aops-wizard-card-${f.id}`,
                      onClick: () => handOffToWizard(f.id),
                    })
                  )}
              </div>
            </div>

            {batchLost && <div className="aops-error">{i18n.t("AreaOps.BatchUnsupported")}</div>}

            {drawFeature && (
              <div className="aops-draw-hint">
                {drawFeature.kind === "region"
                  ? i18n.t("AreaOps.DrawRegionHint")
                  : i18n.t("AreaOps.ClickPointHint")}
                <span className="aops-draw-hint-actions">
                  {drawFeature.wholeImage && (
                    <button
                      type="button"
                      className="aops-link"
                      onClick={() => openWholeDraft(drawFeature.id)}
                    >
                      {i18n.t("AreaOps.ApplyToWholeImage")}
                    </button>
                  )}
                  <button type="button" className="aops-link" onClick={() => setDrawFeature(null)}>
                    {i18n.t("Cancel")}
                  </button>
                </span>
              </div>
            )}

            {uploadError && <div className="aops-error">{uploadError}</div>}
          </div>

          {/* GEÇMİŞ — özelliklerden AYRI, sağa doğru açılan kendi kolonu (istek:
              aşağı değil sağa). Versiyon zinciri; tıklayınca o versiyona döner. */}
          <div className="aops-modal-history" style={colStyle}>
            {/* History / versions — the chain as a clickable version list.
                Click any version (incl. Original) to revert the image to it. */}
            <div className="aops-hist-title">{i18n.t("AreaOps.History")}</div>
            <div className="aops-list">
              {/* Original version */}
              <div
                className={`aops-ver ${currentUrl === trueOriginal ? "is-current" : ""}`}
                title={i18n.t("AreaOps.OriginalVersion")}
              >
                <div className="aops-ver-thumbwrap">
                  <img
                    className="aops-ver-thumb"
                    src={trueOriginal}
                    alt="original"
                    draggable={false}
                  />
                  <span className="aops-ver-vbadge">v1</span>
                </div>
                <span className="aops-ver-label">{i18n.t("AreaOps.OriginalVersion")}</span>
                {currentUrl === trueOriginal ? (
                  <LuCheck size={14} className="aops-ok" />
                ) : (
                  <button
                    type="button"
                    className="aops-ver-btn"
                    title={i18n.t("AreaOps.RevertOriginal")}
                    onClick={revertToOriginal}
                  >
                    <LuArrowRight size={16} />
                  </button>
                )}
              </div>

              {pins.map((pin) => {
                const isBatch = pin.featureId === AREA_OPS_BATCH_OPERATION;
                let name = opName(pin);
                if (isBatch) name = formatAreaOpsBatchLabel(t);

                if (!inHistory(pin)) return null;
                const version = `v${versionNo.get(pin.id)}`;

                // Done — a version row (after the batch's intermediate steps).
                if (pin.status === "done") {
                  return (
                    <Fragment key={pin.id}>
                      {(pin.steps || []).map((step, k) => {
                        const stepFeature = getAreaOpFeature(step.type);
                        return versionRow({
                          key: `${pin.id}:${k}`,
                          url: step.url,
                          name: stepFeature ? i18n.t(stepFeature.name) : step.type,
                          version: `v${versionNo.get(`${pin.id}:${k}`)}`,
                          onRevert: () => revertToVersion(step.url),
                          failed: step.status === "failed",
                        });
                      })}
                      {versionRow({
                        key: pin.id,
                        url: pin.resultUrl,
                        name,
                        version,
                        onRevert: () => revertToPin(pin.id),
                      })}
                    </Fragment>
                  );
                }

                // Running / queued / failed — status rows.
                return (
                  <div key={pin.id} className={`aops-row aops-row--${pin.status}`}>
                    <div className="aops-row-head">
                      <span className={`aops-num aops-num--${pin.status}`}>{version}</span>
                      <span className="aops-name">{name}</span>
                      <span className="aops-status">
                        {pin.status === "running" && (
                          <LuLoaderCircle size={13} className="aops-spin" />
                        )}
                        {pin.status === "failed" && (
                          <LuTriangleAlert size={13} className="aops-bad" />
                        )}
                        {i18n.t(STATUS_LABEL[pin.status] || "AreaOps.StatusQueued")}
                      </span>
                      {/* Sil: hata alan / sırada takılan / süren bir girdiyi
                          kaldır. removePin süren işi abort da eder, böylece
                          "hata" satırı sonsuza kadar durmaz. */}
                      <button
                        type="button"
                        className="aops-row-remove"
                        title={i18n.t("AreaOps.Remove")}
                        aria-label={i18n.t("AreaOps.Remove")}
                        onClick={() => removePin(pin.id)}
                      >
                        <LuX size={13} />
                      </button>
                    </div>
                    {pin.status === "failed" && pin.error && (
                      <div className="aops-error aops-error--row">{pin.error}</div>
                    )}
                  </div>
                );
              })}
            </div>

            {busy && <div className="aops-busy">{i18n.t("AreaOps.Working")}</div>}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
